import { ReactNode, useCallback, useEffect } from 'react'
import toast, { ToastOptions } from 'react-hot-toast'
import { useLDFlags } from '../../hooks'
import { IonIcon } from '../../Icons/IonIcon'
import { cn } from '../../lib'
import { createLogger } from '../../utils/Logger'
import { Button2 } from './button'

const DeprecatedNotification = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return (
    <div
      className={cn(
        'flex gap-2 m-h-8 items-center justify-between shadow-lg rounded-md bg-white',
        'py-2 px-4 text-xs font-medium transition-opacity duration-200',
        className,
      )}
    >
      {children}
    </div>
  )
}

const Notification = ({ children, className }: { children: ReactNode; className?: string }) => {
  return (
    <div
      className={cn(
        'flex gap-3 min-h-16 min-w-72 w-fit max-w-[580px] h-fit items-center justify-between shadow-lg rounded-md bg-brand-dark-blue-s1',
        'py-5 px-5 text-sm font-medium transition-opacity duration-200 text-white/90',
        className,
      )}
    >
      {children}
    </div>
  )
}

const SuccessToastContent = ({
  message,
  toastId,
  duration,
}: {
  message?: string
  toastId: string
  duration: number
}) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      toast.remove(toastId)
    }, duration)

    return () => clearTimeout(timeout)
  }, [toastId, duration])

  return (
    <Notification>
      <div className='flex items-center text-wrap gap-3 w-full h-fit'>
        <IonIcon name='checkmark-circle' className={'text-success-s5 min-h-5 min-w-5'} />
        <span className='text-sm text-white/90 text-wrap'>{message ?? 'Action succeeded.'}</span>
      </div>
      <Button2
        leadingIcon='close'
        variant='ghost-white'
        size={'x-small'}
        className='hover:text-white flex-shrink-0'
        onClick={() => toast.remove(toastId)}
      />
    </Notification>
  )
}

const DeprecatedSuccessToastContent = ({
  message,
  toastId,
  duration,
}: {
  message?: string
  toastId: string
  duration: number
}) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      toast.remove(toastId)
    }, duration)

    return () => clearTimeout(timeout)
  }, [toastId, duration])

  return (
    <DeprecatedNotification>
      <IonIcon name='checkmark-circle-outline' className={'text-success-s5'} />
      {message ?? 'Action succeeded.'}
    </DeprecatedNotification>
  )
}

const ErrorToastContent = ({
  message,
  toastId,
  duration,
}: {
  message?: string
  toastId: string
  duration: number
}) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      toast.remove(toastId)
    }, duration)

    return () => clearTimeout(timeout)
  }, [toastId, duration])

  return (
    <Notification>
      <div className='flex items-center text-wrap gap-3 w-full h-fit'>
        <IonIcon name='warning' className={'text-warning-s5 min-h-5 min-w-5'} />
        <span className='text-sm text-white/90 text-wrap'>{message ?? 'Action had error.'}</span>
      </div>
      <Button2
        leadingIcon='close'
        variant='ghost-white'
        size={'x-small'}
        className='hover:text-white flex-shrink-0'
        onClick={() => toast.remove(toastId)}
      />
    </Notification>
  )
}

const DeprecatedErrorToastContent = ({
  message,
  toastId,
  duration,
}: {
  message?: string
  toastId: string
  duration: number
}) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      toast.remove(toastId)
    }, duration)

    return () => clearTimeout(timeout)
  }, [toastId, duration])

  return (
    <DeprecatedNotification>
      <IonIcon name='alert-circle-outline' className={'text-warning-s5'} />
      {message ?? 'Action had error.'}
    </DeprecatedNotification>
  )
}

export function useNotifications() {
  const { flags } = useLDFlags()

  const success = useCallback(
    (message?: string, customOptions?: ToastOptions) => {
      const duration = customOptions?.duration ?? 2500

      if (flags && flags['new-toast']) {
        toast.custom(
          (t) => <SuccessToastContent message={message} toastId={t.id} duration={duration} />,
          {
            duration: duration,
            position: 'bottom-center',
            ...customOptions,
          },
        )
      } else {
        toast.custom(
          (t) => (
            <DeprecatedSuccessToastContent message={message} toastId={t.id} duration={duration} />
          ),
          {
            duration: duration,
            position: 'bottom-center',
            ...customOptions,
          },
        )
      }
    },
    [flags],
  )

  const error = useCallback(
    (message?: string, customOptions?: ToastOptions, hasBeenLogged = false) => {
      if (!hasBeenLogged && message) {
        const logger = createLogger()
        logger.warn(message)
      }

      const duration = customOptions?.duration ?? 6000

      if (flags && flags['new-toast']) {
        toast.custom(
          (t) => <ErrorToastContent message={message} toastId={t.id} duration={duration} />,
          {
            duration: duration,
            position: 'bottom-center',
            ...customOptions,
          },
        )
      } else {
        toast.custom(
          (t) => (
            <DeprecatedErrorToastContent message={message} toastId={t.id} duration={duration} />
          ),
          {
            duration: duration,
            position: 'bottom-center',
            ...customOptions,
          },
        )
      }
    },
    [flags],
  )

  return {
    success,
    error,
  }
}
